import * as React from 'react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ApiError, getJSON, patchJSON } from '@/lib/api'

export type BulkGroupAction = 'add' | 'remove'

export interface BulkGroupSelection {
  contact_ids?: number[]
  matching?: Record<string, string | boolean>
}

export interface BulkGroupChangeResponse {
  bulk_group_change: { selected_count: number; changed_count: number }
}

export function BulkGroupNotice({
  result,
}: {
  result: BulkGroupChangeResponse['bulk_group_change'] | null
}) {
  if (!result) return null

  return (
    <Alert className="m-4">
      Group change complete: {result.changed_count} of {result.selected_count} selected Contacts
      changed.
    </Alert>
  )
}

interface GroupsResponse {
  groups: Array<{ id: number; name: string }>
}

export function ContactsBulkGroupDialog({
  open,
  onOpenChange,
  action,
  selection,
  selectedCount,
  onApplied,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  action: BulkGroupAction | null
  selection: BulkGroupSelection
  selectedCount: number
  onApplied: (response: BulkGroupChangeResponse) => void
}) {
  const requestedAction = action ?? 'add'
  const [groupId, setGroupId] = React.useState('')
  const [preview, setPreview] = React.useState<BulkGroupChangeResponse | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [pending, setPending] = React.useState(false)
  const [groups, setGroups] = React.useState<GroupsResponse['groups']>([])

  React.useEffect(() => {
    if (!open) return

    setGroupId('')
    setPreview(null)
    setError(null)
    void getJSON<GroupsResponse>('/api/v1/groups')
      .then((response) => setGroups(response.groups))
      .catch(() => setError('Groups unavailable.'))
  }, [open])

  async function submit(confirm: boolean) {
    setPending(true)
    setError(null)
    try {
      const response = confirm
        ? await requestBulkGroupChange(groupId, requestedAction, selection, true)
        : await previewBulkGroupChange(groupId, requestedAction, selection)
      if (confirm) {
        onApplied(response)
        onOpenChange(false)
      } else {
        setPreview(response)
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Group change failed.')
    } finally {
      setPending(false)
    }
  }

  const verb = requestedAction === 'add' ? 'Add to' : 'Remove from'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{verb} Group</DialogTitle>
          <DialogDescription>
            Choose a Group, then confirm the change for {selectedCount} selected Contacts.
          </DialogDescription>
        </DialogHeader>
        {error && <Alert>{error}</Alert>}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="bulk-contact-group">Group</Label>
          <Select
            value={groupId}
            onValueChange={(value) => {
              setGroupId(value)
              setPreview(null)
            }}
          >
            <SelectTrigger id="bulk-contact-group">
              <SelectValue placeholder="Select a Group" />
            </SelectTrigger>
            <SelectContent>
              {groups.map((group) => (
                <SelectItem key={group.id} value={String(group.id)}>
                  {group.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {preview && (
          <div className="rounded-md border border-border bg-accent/40 px-3 py-2 text-body-sm text-muted-foreground">
            {preview.bulk_group_change.selected_count} selected. Confirm to {requestedAction} their
            Group membership.
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!groupId || pending}
            onClick={() => submit(Boolean(preview))}
          >
            {pending ? 'Working…' : preview ? `${verb} Group` : 'Preview change'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

async function previewBulkGroupChange(
  groupId: string,
  action: BulkGroupAction,
  selection: BulkGroupSelection,
) {
  try {
    return await requestBulkGroupChange(groupId, action, selection, false)
  } catch (error) {
    if (error instanceof ApiError && error.status === 409)
      return error.body as BulkGroupChangeResponse
    throw error
  }
}

function requestBulkGroupChange(
  groupId: string,
  action: BulkGroupAction,
  selection: BulkGroupSelection,
  confirm: boolean,
) {
  return patchJSON<BulkGroupChangeResponse>(`/api/v1/groups/${groupId}/members/bulk`, {
    bulk_group_change: { action, ...selection, confirm },
  })
}
